import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";
import { useMsal } from "@azure/msal-react";

const STATUS_COLORS = {
    queued: "#8b8fa3",
    processing: "#3b82f6",
    verifying: "#6366f1",
    uploading: "#8b5cf6",
    completed: "#22c55e",
    failed: "#ef4444",
    retry: "#f59e0b",
    expired: "#5c6078",
};

const STATUS_LABELS = {
    queued: "Queued",
    processing: "Downloading",
    verifying: "Verifying",
    uploading: "Uploading",
    completed: "Ready",
    failed: "Failed",
    retry: "Retrying",
    expired: "Expired",
};

// The requested window is older than the recorder's oldest footage (set by the video worker),
// so nothing was there to pull. Not an outage - shown grey, not red.
const isAgedOut = (clip) => clip.status === "failed" && clip.fail_reason === "aged_out";

function VideoClips() {
    const { accounts } = useMsal();
    const [clips, setClips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [siteFilter, setSiteFilter] = useState("");
    const [formMode, setFormMode] = useState("receipt");
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(null);

    // Store + camera data for site picker
    const [stores, setStores] = useState([]);
    const [nvrSites, setNvrSites] = useState(new Set());
    const [storesLoading, setStoresLoading] = useState(true);
    const [siteSearch, setSiteSearch] = useState("");
    const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);

    // Camera thumbnails from hourly captures
    const [cameraThumbs, setCameraThumbs] = useState([]);
    const [thumbsLoading, setThumbsLoading] = useState(false);

    // Form fields
    const [formSiteId, setFormSiteId] = useState("");
    const [formSiteName, setFormSiteName] = useState("");
    const [selectedChannels, setSelectedChannels] = useState(new Set([1]));
    const [formEmail, setFormEmail] = useState(accounts[0]?.username || "");

    // Custom mode fields
    const [formDate, setFormDate] = useState(new Date().toISOString().slice(0, 10));
    const [formStartTime, setFormStartTime] = useState("09:00");
    const [formEndTime, setFormEndTime] = useState("09:30");

    // Receipt mode fields
    const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
    const [receiptTime, setReceiptTime] = useState("12:00");

    const loadClips = useCallback(() => {
        const params = {};
        if (statusFilter) params.status = statusFilter;
        params.limit = 100;
        api.videoClips(params)
            .then((data) => { setClips(data); setLoading(false); })
            .catch(() => setLoading(false));
    }, [statusFilter]);

    useEffect(() => { loadClips(); }, [loadClips]);

    const filteredClips = useMemo(() => {
        if (!siteFilter) return clips;
        return clips.filter(c => String(c.site_id).includes(siteFilter));
    }, [clips, siteFilter]);

    // Load sites from video-clips/sites endpoint (no pages.stores permission needed)
    useEffect(() => {
        api.videoClipSites().catch(() => []).then((sites) => {
            setStores((sites || []).map(s => ({ siteId: s.siteId, name: s.name, state: s.state })));
            const camSites = new Set();
            (sites || []).forEach(s => { if (s.hasCamera) camSites.add(String(s.siteId)); });
            setNvrSites(camSites);
            setStoresLoading(false);
        });
    }, []);

    // Load camera thumbnails when site is selected
    useEffect(() => {
        if (!formSiteId) { setCameraThumbs([]); return; }
        setThumbsLoading(true);
        const today = new Date().toISOString().slice(0, 10);
        api.hourlyImages(formSiteId, today)
            .then((data) => {
                const times = data.times || [];
                if (times.length > 0) {
                    // Use the latest time slot's cameras
                    const latest = times[times.length - 1];
                    setCameraThumbs(latest.cameras || []);
                } else if (data.availableDates && data.availableDates.length > 0) {
                    // No images for today, try the most recent available date
                    return api.hourlyImages(formSiteId, data.availableDates[0]).then((fallback) => {
                        const fbTimes = fallback.times || [];
                        if (fbTimes.length > 0) {
                            // Find a midday-ish slot, or use the latest
                            const middaySlot = fbTimes.find((t) => t.time >= "1100" && t.time <= "1400") || fbTimes[fbTimes.length - 1];
                            setCameraThumbs(middaySlot.cameras || []);
                        } else {
                            setCameraThumbs([]);
                        }
                    });
                } else {
                    setCameraThumbs([]);
                }
            })
            .catch(() => setCameraThumbs([]))
            .finally(() => setThumbsLoading(false));
    }, [formSiteId]);

    // Auto-refresh active clips every 10 seconds
    useEffect(() => {
        const hasActive = clips.some((c) =>
            ["queued", "processing", "verifying", "uploading"].includes(c.status)
        );
        if (!hasActive) return;
        const timer = setInterval(loadClips, 10000);
        return () => clearInterval(timer);
    }, [clips, loadClips]);

    // Filtered store list for dropdown
    const filteredStores = useMemo(() => {
        if (!siteSearch) return stores.slice(0, 50);
        const q = siteSearch.toLowerCase();
        return stores.filter((s) =>
            String(s.siteId).includes(q) ||
            (s.name || "").toLowerCase().includes(q) ||
            (s.state || "").toLowerCase().includes(q)
        ).slice(0, 50);
    }, [stores, siteSearch]);

    const toggleChannel = (ch) => {
        setSelectedChannels((prev) => {
            const next = new Set(prev);
            if (next.has(ch)) { next.delete(ch); } else { next.add(ch); }
            return next;
        });
    };

    const selectSite = (store) => {
        setFormSiteId(String(store.siteId));
        setFormSiteName(store.name);
        setSelectedChannels(hasCameras(store.siteId) ? new Set([1]) : new Set());
        setSiteSearch("");
        setSiteDropdownOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);
        setSubmitSuccess(null);

        if (!formSiteId || !/^\d{4}$/.test(formSiteId)) {
            setSubmitError("Please select a valid site");
            return;
        }
        if (selectedChannels.size === 0) {
            setSubmitError("Please select at least one camera channel");
            return;
        }

        setSubmitting(true);

        try {
            let startTime, endTime;
            let crossesMidnight = false;

            // Convert local time to UTC ISO string for the API
            // The video downloader expects UTC and converts to site-local time
            const toUtcIso = (d) => d.toISOString().replace(/\.\d{3}Z$/, "Z");

            if (formMode === "receipt") {
                const receiptDateTime = new Date(`${receiptDate}T${receiptTime}:00`);
                const start = new Date(receiptDateTime.getTime() - 20 * 60000);
                const end = new Date(receiptDateTime.getTime() + 10 * 60000);
                startTime = toUtcIso(start);
                endTime = toUtcIso(end);
            } else {
                const rangeStart = new Date(`${formDate}T${formStartTime}:00`);
                const rangeEnd = new Date(`${formDate}T${formEndTime}:00`);
                // An end at or before the start means the window runs past midnight.
                // 00:00 to 00:00 is how a whole day is expressed.
                if (rangeEnd <= rangeStart) {
                    rangeEnd.setDate(rangeEnd.getDate() + 1);
                    crossesMidnight = true;
                }
                startTime = toUtcIso(rangeStart);
                endTime = toUtcIso(rangeEnd);
            }

            // Submit one request per selected channel
            const channels = [...selectedChannels].sort((a, b) => a - b);
            const results = [];
            for (const ch of channels) {
                const result = await api.requestVideoClip({
                    siteId: formSiteId,
                    channel: ch,
                    startTime,
                    endTime,
                    email: formEmail,
                });
                results.push(result);
            }

            const ids = results.map((r) => `#${r.id}`).join(", ");
            const chList = channels.map((c) => `Ch${c}`).join(", ");
            const timeDesc = formMode === "receipt"
                ? `Receipt: ${receiptDate} ${receiptTime} (20m before, 10m after)`
                : `${formDate} ${formStartTime}\u2013${formEndTime}${crossesMidnight ? " (next day)" : ""}`;
            setSubmitSuccess(`${results.length} request${results.length > 1 ? "s" : ""} (${ids}) submitted for site ${formSiteId} ${chList} ${"\u2014"} ${timeDesc}`);
            loadClips();
        } catch (err) {
            setSubmitError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownload = async (clipId) => {
        try {
            const result = await api.videoClipDownload(clipId);
            if (result.url) {
                window.location.href = result.url;
            }
        } catch (err) {
            alert("Download failed: " + err.message);
        }
    };

    const hasCameras = (siteId) => nvrSites.has(String(siteId));

    return (
        <div>
            <div className="page-header">
                <h2>Video Clips</h2>
                <p>Request and download NVR video clips</p>
            </div>

            {/* Success banner */}
            {submitSuccess && (
                <div style={{
                    padding: "12px 16px", marginBottom: 16, borderRadius: 8,
                    background: "#22c55e20", border: "1px solid #22c55e40", color: "#22c55e",
                }}>
                    {submitSuccess}
                </div>
            )}

            {/* New request form — always visible */}
            <div className="card" style={{ marginBottom: 24, padding: 24 }}>
                <div className="card-title" style={{ marginBottom: 16 }}>New Clip Request</div>

                {/* Mode toggle */}
                <div style={{ display: "flex", gap: 0, marginBottom: 20 }}>
                    {[
                        { key: "receipt", label: "Receipt Lookup" },
                        { key: "custom", label: "Custom Range" },
                    ].map((m) => (
                        <button
                            key={m.key}
                            onClick={() => setFormMode(m.key)}
                            style={{
                                padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                                border: "1px solid var(--border)",
                                borderRight: m.key === "receipt" ? "none" : undefined,
                                borderRadius: m.key === "receipt" ? "6px 0 0 6px" : "0 6px 6px 0",
                                background: formMode === m.key ? "var(--primary)" : "var(--bg-hover)",
                                color: formMode === m.key ? "#fff" : "var(--text-secondary)",
                            }}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Row 1: Site picker + Email */}
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr", gap: 16, marginBottom: 16 }}>
                        {/* Site picker */}
                        <div style={{ position: "relative" }}>
                            <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                                Site
                            </label>
                            {storesLoading ? (
                                <div className="filter-select" style={{ width: "100%", color: "var(--text-muted)" }}>
                                    Loading sites...
                                </div>
                            ) : (
                                <>
                                    <input
                                        type="text"
                                        value={siteDropdownOpen ? siteSearch : (formSiteId ? `${formSiteId} \u2014 ${formSiteName}` : "")}
                                        onChange={(e) => { setSiteSearch(e.target.value); setSiteDropdownOpen(true); }}
                                        onFocus={() => setSiteDropdownOpen(true)}
                                        placeholder="Search by name or site ID..."
                                        className="filter-select"
                                        style={{ width: "100%" }}
                                        autoComplete="off"
                                    />
                                    {formSiteId && !siteDropdownOpen && (
                                        <span style={{
                                            position: "absolute", right: 8, top: 28,
                                            width: 8, height: 8, borderRadius: "50%",
                                            background: hasCameras(formSiteId) ? "#22c55e" : "#ef4444",
                                        }} />
                                    )}
                                    {siteDropdownOpen && (
                                        <>
                                            <div
                                                style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
                                                onClick={() => setSiteDropdownOpen(false)}
                                            />
                                            <div style={{
                                                position: "absolute", top: "100%", left: 0, right: 0,
                                                maxHeight: 280, overflowY: "auto", zIndex: 100,
                                                background: "var(--bg-card)", border: "1px solid var(--border)",
                                                borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                                            }}>
                                                {filteredStores.length === 0 && (
                                                    <div style={{ padding: 12, color: "var(--text-muted)", fontSize: 13 }}>
                                                        No sites found
                                                    </div>
                                                )}
                                                {filteredStores.map((s) => {
                                                    const hasCam = hasCameras(s.siteId);
                                                    return (
                                                        <div
                                                            key={s.siteId}
                                                            onClick={() => selectSite(s)}
                                                            style={{
                                                                padding: "8px 12px", cursor: "pointer",
                                                                display: "flex", alignItems: "center", gap: 8,
                                                                borderBottom: "1px solid var(--border)",
                                                                opacity: hasCam ? 1 : 0.5,
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg-hover)"}
                                                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                                        >
                                                            <span style={{
                                                                width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
                                                                background: hasCam ? "#22c55e" : "#5c6078",
                                                            }} />
                                                            <span style={{ fontWeight: 600, fontSize: 13, minWidth: 40 }}>
                                                                {s.siteId}
                                                            </span>
                                                            <span style={{ fontSize: 13, flex: 1 }}>{s.name}</span>
                                                            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.state}</span>
                                                            {!hasCam && (
                                                                <span style={{ fontSize: 10, color: "#ef4444", whiteSpace: "nowrap" }}>
                                                                    No NVR
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {stores.length > 50 && filteredStores.length >= 50 && (
                                                    <div style={{ padding: 8, color: "var(--text-muted)", fontSize: 11, textAlign: "center" }}>
                                                        Type to search {stores.length} sites...
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        <div>
                            <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                                Email (for notification)
                            </label>
                            <input
                                type="email"
                                value={formEmail}
                                onChange={(e) => setFormEmail(e.target.value)}
                                placeholder="name@example.com"
                                required
                                className="filter-select"
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>

                    {/* NVR not available banner */}
                    {formSiteId && !hasCameras(formSiteId) && (
                        <div style={{
                            padding: "14px 16px", marginBottom: 16, borderRadius: 8,
                            background: "rgba(239, 68, 68, 0.08)", border: "1px solid rgba(239, 68, 68, 0.25)",
                            color: "#ef4444", fontSize: 13, fontWeight: 600, textAlign: "center",
                        }}>
                            NVR not available for this site — video clips cannot be requested
                        </div>
                    )}

                    {formSiteId && hasCameras(formSiteId) && cameraThumbs.length === 0 && !thumbsLoading && (
                        <div style={{
                            padding: "14px 16px", marginBottom: 16, borderRadius: 8,
                            background: "rgba(239, 68, 68, 0.08)", border: "1px solid rgba(239, 68, 68, 0.25)",
                            color: "#ef4444", fontSize: 13, fontWeight: 600, textAlign: "center",
                        }}>
                            No camera previews available for this site — cameras may be offline or unreachable
                        </div>
                    )}

                    {/* Camera channel picker — thumbnails from hourly captures */}
                    {formSiteId && hasCameras(formSiteId) && (
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
                                Camera Channels
                                {selectedChannels.size > 0 && (
                                    <span style={{ color: "#22c55e", fontWeight: 600, marginLeft: 8 }}>
                                        ({selectedChannels.size} selected: {[...selectedChannels].sort((a,b) => a-b).map(c => `Ch${c}`).join(", ")})
                                    </span>
                                )}
                            </label>
                            {thumbsLoading ? (
                                <div style={{ padding: 12, color: "var(--text-muted)", fontSize: 13 }}>Loading camera previews...</div>
                            ) : cameraThumbs.length > 0 ? (
                                <>
                                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                                        <button type="button" onClick={() => setSelectedChannels(new Set(cameraThumbs.map(c => c.camera)))}
                                            style={{ fontSize: 11, padding: "3px 10px", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-hover)", color: "var(--text-secondary)", cursor: "pointer" }}>
                                            Select All
                                        </button>
                                        <button type="button" onClick={() => setSelectedChannels(new Set())}
                                            style={{ fontSize: 11, padding: "3px 10px", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-hover)", color: "var(--text-secondary)", cursor: "pointer" }}>
                                            Clear
                                        </button>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                        {cameraThumbs.map((cam) => {
                                            const isSelected = selectedChannels.has(cam.camera);
                                            return (
                                                <div
                                                    key={cam.camera}
                                                    onClick={() => toggleChannel(cam.camera)}
                                                    style={{
                                                        cursor: "pointer",
                                                        borderRadius: 8,
                                                        overflow: "hidden",
                                                        border: isSelected
                                                            ? "3px solid #22c55e"
                                                            : "2px solid var(--border)",
                                                        position: "relative",
                                                        transition: "border-color 0.15s",
                                                        boxShadow: isSelected ? "0 0 8px rgba(34,197,94,0.4)" : "none",
                                                    }}
                                                >
                                                    <img
                                                        src={cam.url}
                                                        alt={`Camera ${cam.camera}`}
                                                        style={{
                                                            width: 160, height: 110, objectFit: "cover", display: "block",
                                                            opacity: isSelected ? 1 : 0.5,
                                                        }}
                                                        loading="lazy"
                                                    />
                                                    <div style={{
                                                        position: "absolute", bottom: 0, left: 0, right: 0,
                                                        background: isSelected ? "#22c55e" : "rgba(0,0,0,0.7)",
                                                        padding: "4px 8px",
                                                        fontSize: 12, color: "#fff", fontWeight: 600,
                                                        textAlign: "center",
                                                    }}>
                                                        {isSelected ? "\u2713 " : ""}Ch {cam.camera}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div>
                                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
                                        No camera previews available. Select channels:
                                    </div>
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                                        <button type="button" onClick={() => setSelectedChannels(new Set(Array.from({ length: 16 }, (_, i) => i + 1)))}
                                            style={{ fontSize: 11, padding: "3px 10px", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-hover)", color: "var(--text-secondary)", cursor: "pointer" }}>
                                            Select All
                                        </button>
                                        <button type="button" onClick={() => setSelectedChannels(new Set())}
                                            style={{ fontSize: 11, padding: "3px 10px", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-hover)", color: "var(--text-secondary)", cursor: "pointer" }}>
                                            Clear
                                        </button>
                                    </div>
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                        {Array.from({ length: 16 }, (_, i) => i + 1).map(ch => {
                                            const isSelected = selectedChannels.has(ch);
                                            return (
                                                <button
                                                    key={ch} type="button"
                                                    onClick={() => toggleChannel(ch)}
                                                    style={{
                                                        width: 48, height: 36, borderRadius: 6, cursor: "pointer",
                                                        fontSize: 13, fontWeight: 600,
                                                        border: isSelected ? "2px solid #22c55e" : "1px solid var(--border)",
                                                        background: isSelected ? "rgba(34,197,94,0.15)" : "var(--bg-hover)",
                                                        color: isSelected ? "#22c55e" : "var(--text-secondary)",
                                                    }}
                                                >
                                                    {ch}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Time selection — depends on mode */}
                    {formMode === "receipt" ? (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{
                                padding: "12px 16px", marginBottom: 12, borderRadius: 6,
                                background: "var(--bg-hover)", border: "1px solid var(--border)",
                                fontSize: 12, color: "var(--text-secondary)",
                            }}>
                                Enter the date and time from the receipt. The system will automatically request video from <strong>20 minutes before</strong> to <strong>10 minutes after</strong> the receipt time (30 min total).
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                                <div>
                                    <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                                        Receipt Date
                                    </label>
                                    <input
                                        type="date"
                                        value={receiptDate}
                                        onChange={(e) => setReceiptDate(e.target.value)}
                                        required
                                        className="filter-select"
                                        style={{ width: "100%" }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                                        Receipt Time
                                    </label>
                                    <input
                                        type="time"
                                        value={receiptTime}
                                        onChange={(e) => setReceiptTime(e.target.value)}
                                        required
                                        className="filter-select"
                                        style={{ width: "100%" }}
                                    />
                                </div>
                                <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 2 }}>
                                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                        {receiptDate && receiptTime && (() => {
                                            const rt = new Date(`${receiptDate}T${receiptTime}:00`);
                                            const s = new Date(rt.getTime() - 20 * 60000);
                                            const e = new Date(rt.getTime() + 10 * 60000);
                                            const fmt = (d) => d.toTimeString().slice(0, 5);
                                            return `Clip: ${fmt(s)} \u2014 ${fmt(e)} (30 min)`;
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
                            <div>
                                <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                                    Date
                                </label>
                                <input
                                    type="date"
                                    value={formDate}
                                    onChange={(e) => setFormDate(e.target.value)}
                                    required
                                    className="filter-select"
                                    style={{ width: "100%" }}
                                />
                            </div>
                            <div>
                                <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                                    Start Time
                                </label>
                                <input
                                    type="time"
                                    value={formStartTime}
                                    onChange={(e) => setFormStartTime(e.target.value)}
                                    required
                                    className="filter-select"
                                    style={{ width: "100%" }}
                                />
                            </div>
                            <div>
                                <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                                    End Time (max 24h; same or earlier = next day)
                                </label>
                                <input
                                    type="time"
                                    value={formEndTime}
                                    onChange={(e) => setFormEndTime(e.target.value)}
                                    required
                                    className="filter-select"
                                    style={{ width: "100%" }}
                                />
                            </div>
                        </div>
                    )}

                    {submitError && (
                        <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>
                            {submitError}
                        </div>
                    )}

                    {/* Prominent submit button */}
                    <button
                        type="submit"
                        disabled={submitting || !formSiteId || selectedChannels.size === 0 || !hasCameras(formSiteId)}
                        style={{
                            width: "100%",
                            padding: "14px 32px",
                            borderRadius: 8,
                            border: "none",
                            background: submitting || !formSiteId || selectedChannels.size === 0 || !hasCameras(formSiteId) ? "var(--text-muted)" : "var(--primary)",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 16,
                            cursor: submitting || !formSiteId || selectedChannels.size === 0 || !hasCameras(formSiteId) ? "not-allowed" : "pointer",
                            letterSpacing: "0.5px",
                        }}
                    >
                        {submitting ? "Submitting..." : `\uD83C\uDFAC ${formMode === "receipt" ? "Request Receipt Clip" : "Submit Clip Request"}${selectedChannels.size > 1 ? ` (${selectedChannels.size} cameras)` : ""}`}
                    </button>
                </form>
            </div>

            {/* Filters + clip history */}
            <div className="filter-row" style={{ marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input
                    type="text"
                    className="filter-select"
                    placeholder="Filter by site ID..."
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.target.value)}
                    style={{ minWidth: 160 }}
                />
                <select
                    className="filter-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="">All Statuses</option>
                    <option value="queued">Queued</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="retry">Retrying</option>
                </select>
                <button
                    onClick={loadClips}
                    style={{
                        padding: "6px 14px", borderRadius: 6, border: "1px solid var(--border)",
                        background: "var(--bg-hover)", color: "var(--text-secondary)", cursor: "pointer", fontSize: 13,
                    }}
                >
                    Refresh
                </button>
            </div>

            {loading && <LoadingSpinner message="Loading video clips..." />}

            {!loading && (
                <div className="card">
                    <div className="card-title">Previous Requests ({filteredClips.length})</div>
                    {filteredClips.length > 0 ? (
                        <table className="store-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Site</th>
                                    <th>Ch</th>
                                    <th>Time Range</th>
                                    <th>Status</th>
                                    <th>Progress</th>
                                    <th>Size</th>
                                    <th>Verified</th>
                                    <th>Requested</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredClips.map((clip) => (
                                    <tr key={clip.id}>
                                        <td style={{ color: "var(--text-muted)", fontSize: 12 }}>#{clip.id}</td>
                                        <td style={{ fontWeight: 600 }}>{clip.site_id}</td>
                                        <td>{clip.channel}</td>
                                        <td style={{ fontSize: 12 }}>
                                            <div>{new Date(clip.start_time).toLocaleString("en-AU", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: clip.timezone || "Australia/Melbourne" })}</div>
                                            <div style={{ color: "var(--text-muted)" }}>
                                                to {new Date(clip.end_time).toLocaleString("en-AU", { hour: "2-digit", minute: "2-digit", timeZone: clip.timezone || "Australia/Melbourne" })}
                                                {clip.duration_seconds && ` (${clip.duration_seconds}s)`}
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{
                                                color: isAgedOut(clip) ? STATUS_COLORS.expired : (STATUS_COLORS[clip.status] || "var(--text-muted)"),
                                                fontWeight: 600, fontSize: 12, textTransform: "uppercase",
                                            }}>
                                                {isAgedOut(clip) ? "Aged out" : (STATUS_LABELS[clip.status] || clip.status)}
                                            </span>
                                        </td>
                                        <td>
                                            {["processing", "verifying", "uploading"].includes(clip.status) ? (
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <div style={{
                                                        height: 6, width: 60, background: "var(--bg-hover)",
                                                        borderRadius: 3, overflow: "hidden",
                                                    }}>
                                                        <div style={{
                                                            height: "100%", width: `${clip.progress}%`,
                                                            background: STATUS_COLORS[clip.status],
                                                            borderRadius: 3, transition: "width 0.3s",
                                                        }} />
                                                    </div>
                                                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                                                        {clip.progress}%
                                                    </span>
                                                </div>
                                            ) : clip.status === "completed" ? (
                                                <span style={{ color: "#22c55e", fontSize: 12 }}>100%</span>
                                            ) : isAgedOut(clip) ? (
                                                <span style={{ color: "var(--text-muted)", fontSize: 11 }} title={clip.error_message}>
                                                    No longer on recorder
                                                </span>
                                            ) : clip.status === "failed" ? (
                                                <span style={{ color: "#ef4444", fontSize: 11 }} title={clip.error_message}>
                                                    {clip.error_message ? (
                                                        clip.error_message.toLowerCase().includes('path') || clip.error_message.toLowerCase().includes('not found') || clip.error_message.toLowerCase().includes('no files')
                                                            ? "No recording found"
                                                            : clip.error_message.toLowerCase().includes('timeout')
                                                                ? "Download timed out"
                                                                : clip.error_message.length > 50
                                                                    ? clip.error_message.slice(0, 47) + "..."
                                                                    : clip.error_message
                                                    ) : "Failed"}
                                                </span>
                                            ) : clip.status === "retry" ? (
                                                <span style={{ color: "#f59e0b", fontSize: 11 }} title={clip.error_message}>
                                                    Retry {clip.retry_count}/3
                                                    {clip.next_retry_at && ` at ${new Date(clip.next_retry_at).toLocaleString("en-AU", { hour: "2-digit", minute: "2-digit", timeZone: clip.timezone || "Australia/Melbourne" })}`}
                                                    <div style={{ fontSize: 10, color: "#d97706" }}>{clip.error_message || ""}</div>
                                                </span>
                                            ) : (
                                                <span style={{ color: "var(--text-muted)", fontSize: 12 }}>{"\u2014"}</span>
                                            )}
                                        </td>
                                        <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                            {clip.file_size_bytes
                                                ? `${(clip.file_size_bytes / 1048576).toFixed(1)} MB`
                                                : "\u2014"}
                                        </td>
                                        <td>
                                            {clip.verification_ok === true && (
                                                <span style={{ color: "#22c55e", fontSize: 12 }} title={clip.verification_note}>OK</span>
                                            )}
                                            {clip.verification_ok === false && (
                                                <span style={{ color: "#f97316", fontSize: 12 }} title={clip.verification_note}>WARN</span>
                                            )}
                                            {clip.verification_ok == null && (
                                                <span style={{ color: "var(--text-muted)", fontSize: 12 }}>{"\u2014"}</span>
                                            )}
                                        </td>
                                        <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                            <div>{new Date(clip.requested_at).toLocaleDateString("en-AU")}</div>
                                            <div>{clip.requested_by}</div>
                                        </td>
                                        <td>
                                            {clip.status === "completed" && (() => {
                                                const expiresAt = clip.expires_at ? new Date(clip.expires_at) : null;
                                                const now = new Date();
                                                const daysLeft = expiresAt ? Math.ceil((expiresAt - now) / 86400000) : null;
                                                const expired = daysLeft !== null && daysLeft <= 0;
                                                return (
                                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                        {!expired && (
                                                            <button
                                                                onClick={() => handleDownload(clip.id)}
                                                                style={{
                                                                    fontSize: 12, padding: "4px 10px", borderRadius: 4,
                                                                    border: "none", background: "#22c55e", color: "#fff",
                                                                    cursor: "pointer", fontWeight: 600,
                                                                }}
                                                            >
                                                                Download
                                                            </button>
                                                        )}
                                                        {daysLeft !== null && (
                                                            <span style={{
                                                                fontSize: 10, fontWeight: 600, padding: "2px 6px",
                                                                borderRadius: 4, whiteSpace: "nowrap",
                                                                background: expired ? "rgba(239,68,68,0.12)" :
                                                                    daysLeft <= 3 ? "rgba(249,115,22,0.12)" : "rgba(34,197,94,0.12)",
                                                                color: expired ? "#ef4444" : daysLeft <= 3 ? "#f97316" : "#22c55e",
                                                            }}>
                                                                {expired ? "Expired" : `Expires in ${daysLeft}d`}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            {clip.status === "expired" && (
                                                <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 600 }}>Expired</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="empty-state" style={{ padding: 24 }}>
                            <p style={{ color: "var(--text-muted)" }}>
                                No clip requests yet. Use the form above to request a video clip.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default VideoClips;
